/**
 * User Preferences API service — recent searches and bookmarks.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

// ── Recent Searches ─────────────────────────────────────────────

export interface RecentSearch {
  searchId: string;
  query: string;
  resultCount: number;
  filters: { repo?: string; type?: string; project?: string };
  createdAt: string;
}

// ── Bookmarks ───────────────────────────────────────────────────

export interface Bookmark {
  docIdHash: string;
  docId: string;
  title: string;
  repoFullName: string;
  path: string;
  docType: string;
  folderId: string | null;
  note: string | null;
  createdAt: string;
}

export interface BookmarkFolder {
  folderId: string;
  name: string;
  color: string | null;
  sortOrder: number;
  createdAt: string;
}

export const userPrefsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  // ── Recent Searches ───────────────────────────────────────────

  async listRecentSearches(): Promise<RecentSearch[]> {
    const { data } = await api.get('/user-prefs/search/recent');
    return data.searches || [];
  },

  async recordSearch(query: string, resultCount: number, filters?: { repo?: string; type?: string; project?: string }): Promise<RecentSearch> {
    const { data } = await api.post('/user-prefs/search/recent', {
      query,
      resultCount,
      filters: filters || {},
    });
    return data;
  },

  async deleteRecentSearch(searchId: string): Promise<void> {
    await api.delete(`/user-prefs/search/recent/${searchId}`);
  },

  async clearRecentSearches(): Promise<void> {
    await api.delete('/user-prefs/search/recent');
  },

  // ── Bookmarks ─────────────────────────────────────────────────

  async listBookmarks(folder?: string): Promise<Bookmark[]> {
    const params = folder ? { folder } : undefined;
    const { data } = await api.get('/user-prefs/bookmarks', { params });
    return data.bookmarks || [];
  },

  async createBookmark(bookmark: {
    docId: string;
    title: string;
    repoFullName: string;
    path: string;
    docType: string;
    folderId?: string;
  }): Promise<Bookmark> {
    const { data } = await api.post('/user-prefs/bookmarks', bookmark);
    return data;
  },

  async updateBookmark(docIdHash: string, updates: { folderId?: string | null; note?: string | null }): Promise<Bookmark> {
    const { data } = await api.put(`/user-prefs/bookmarks/${docIdHash}`, updates);
    return data;
  },

  async deleteBookmark(docIdHash: string): Promise<void> {
    await api.delete(`/user-prefs/bookmarks/${docIdHash}`);
  },

  // ── Bookmark Folders ──────────────────────────────────────────

  async listBookmarkFolders(): Promise<BookmarkFolder[]> {
    const { data } = await api.get('/user-prefs/bookmarks/folders');
    return data.folders || [];
  },

  async createBookmarkFolder(name: string, color?: string): Promise<BookmarkFolder> {
    const { data } = await api.post('/user-prefs/bookmarks/folders', { name, color });
    return data;
  },

  async updateBookmarkFolder(folderId: string, updates: { name?: string; color?: string; sortOrder?: number }): Promise<BookmarkFolder> {
    const { data } = await api.put(`/user-prefs/bookmarks/folders/${folderId}`, updates);
    return data;
  },

  async deleteBookmarkFolder(folderId: string): Promise<void> {
    await api.delete(`/user-prefs/bookmarks/folders/${folderId}`);
  },
};
